import { useCallback, useEffect, useRef, useState } from 'react'

/** Tailwind `lg` — desktop nav is only considered at this width and up */
export const HEADER_NAV_LG_MIN_PX = 1024

const OVERLAP_GAP_PX = 12

/**
 * Detects when the horizontal nav is wider than the space between logo and language switcher.
 * Works even after the nav is hidden (uses last measured nav width).
 */
export function useHeaderNavOverlap(deps = []) {
  const logoRef = useRef(null)
  const navRef = useRef(null)
  const langRef = useRef(null)
  const navWidthRef = useRef(0)
  const [navOverlapsLogo, setNavOverlapsLogo] = useState(false)
  const [overlapViewportWidth, setOverlapViewportWidth] = useState(null)
  const prevOverlapsRef = useRef(false)

  const checkOverlap = useCallback(() => {
    const isLgUp = window.matchMedia(`(min-width: ${HEADER_NAV_LG_MIN_PX}px)`).matches
    if (!isLgUp) {
      setNavOverlapsLogo(false)
      setOverlapViewportWidth(null)
      prevOverlapsRef.current = false
      return
    }

    const logoEl = logoRef.current
    const navEl = navRef.current
    const langEl = langRef.current
    if (!logoEl || !langEl) return

    if (navEl && navEl.offsetParent !== null) {
      navWidthRef.current = navEl.getBoundingClientRect().width
    }

    const logoRect = logoEl.getBoundingClientRect()
    const langRect = langEl.getBoundingClientRect()
    const navRect = navEl.getBoundingClientRect()
    const available = langRect.left - logoRect.right - OVERLAP_GAP_PX
    const navWidth = navWidthRef.current
    const overlaps =
      navWidth > available || navRect.left < logoRect.right + OVERLAP_GAP_PX

    setNavOverlapsLogo(overlaps)

    if (overlaps && !prevOverlapsRef.current) {
      const width = Math.round(window.innerWidth)
      setOverlapViewportWidth(width)
      if (import.meta.env.DEV) {
        console.info(
          `[Header] Nav overlaps logo at ${width}px viewport (nav ~${Math.round(navWidth)}px, space ~${Math.round(available)}px between logo and language)`
        )
      }
    }

    if (!overlaps && prevOverlapsRef.current && import.meta.env.DEV) {
      console.info(`[Header] Nav fits beside logo at ${Math.round(window.innerWidth)}px viewport`)
    }

    prevOverlapsRef.current = overlaps
  }, [])

  const [isLgUp, setIsLgUp] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(`(min-width: ${HEADER_NAV_LG_MIN_PX}px)`).matches
  )

  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${HEADER_NAV_LG_MIN_PX}px)`)
    const onMqChange = () => {
      setIsLgUp(mq.matches)
      checkOverlap()
    }
    mq.addEventListener('change', onMqChange)
    setIsLgUp(mq.matches)

    checkOverlap()
    window.addEventListener('resize', checkOverlap)

    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(checkOverlap) : null
    if (logoRef.current) ro?.observe(logoRef.current)
    if (navRef.current) ro?.observe(navRef.current)
    if (langRef.current) ro?.observe(langRef.current)

    return () => {
      mq.removeEventListener('change', onMqChange)
      window.removeEventListener('resize', checkOverlap)
      ro?.disconnect()
    }
  }, [checkOverlap, ...deps])

  const showDesktopNav = isLgUp && !navOverlapsLogo
  const useCompactNav = !isLgUp || navOverlapsLogo

  return {
    logoRef,
    navRef,
    langRef,
    navOverlapsLogo,
    overlapViewportWidth,
    showDesktopNav,
    useCompactNav,
  }
}
